import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useAuth } from "@/providers";
import type { ViewRecord } from "@/shared/domain/views";
import { viewQueryKeys } from "@/shared/domain/views/hooks";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";

import type { UseResourceViewOptions } from "../useResourceView";
import { definitionToViewState } from "../viewSerialization";

export interface LinkedViewSeed {
  /**
   * The View the page was built from, or `null` if it was built from defaults.
   *
   * When this is set, the table already shows the View's filters, sort and
   * columns from its very first render. The picker uses it to mark the View as
   * loaded straight away and to skip the usual "fetch the View, then apply it"
   * step, which would otherwise re-apply a View the table is already showing.
   *
   * `null` means the table started unfiltered. That happens when the URL has
   * no `?view=`, the feature flag is off, the link points at a View that could
   * not be found, or the View is a RoboQL one (see below). In all of those
   * cases the picker falls back to fetching and applying the View as normal.
   */
  view: ViewRecord | null;
  /**
   * Merge the View's settings into the options a list passes to
   * `useResourceView`, so the table starts out filtered.
   *
   * Safe to call even when there is no View: it hands back `options`
   * unchanged. `useResourceView` only reads these options once, on mount, so
   * this is the one chance to start the table in the right state rather than
   * correcting it a moment later.
   */
  seed: <TData>(
    options: UseResourceViewOptions<TData>,
  ) => UseResourceViewOptions<TData>;
}

/** What was seeded, kept together so `seed` never re-derives it. */
interface Seeded {
  record: ViewRecord;
  restored: ReturnType<typeof definitionToViewState>;
}

/**
 * Start a resource table in the state a `?view=` link asks for, instead of
 * starting it empty and fixing it up afterwards.
 *
 * Why this exists: without it, opening a shared link mounted the table with no
 * filters, fetched a page of everything, then fetched the View, then fetched
 * again with the View's filters. The person following the link watched the
 * wrong rows arrive and get replaced. The route loader now puts the View in
 * the query cache before the page renders, and this hook reads it from the
 * cache during the first render, before the table sends its first request.
 * There is no second fetch, so nothing jumps.
 *
 * It only looks at the URL once, when the page mounts. If `?view=` changes
 * later, that is the user picking a View from the picker, and the normal apply
 * path handles it. Reacting here as well would fight that.
 *
 * RoboQL Views are skipped. Restoring one means turning its query into
 * filters, and only the server can do that, so there is nothing to hand the
 * table synchronously. Those go through the normal fetch-then-apply path,
 * which is one round trip rather than two because the record is already
 * cached, and they still shift once.
 */
export function useLinkedViewSeed(): LinkedViewSeed {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  const linkedId = searchParams.get(VIEW_SEARCH_PARAM);
  const orgId = currentOrganization?.org_id;

  const [seeded] = React.useState<Seeded | null>(() => {
    if (linkedId === null || orgId === undefined) {
      return null;
    }

    const record = queryClient.getQueryData<ViewRecord>(
      viewQueryKeys.id(orgId, linkedId),
    );
    if (record === undefined) {
      // The loader did not warm this slot: no flag, no org, or a link that
      // does not resolve. The deep link will report whichever it was.
      return null;
    }

    let restored;
    try {
      restored = definitionToViewState(record.definition);
    } catch {
      // Written under a newer scheme. `useApplyView` raises the same failure
      // on the apply path, which is where the customer gets told about it.
      return null;
    }

    if (restored.filters.type === "roboql") {
      return null;
    }

    // A View with no filters would be unloaded the instant it was seeded:
    // `useLoadedView` holds "a table showing no filters has no View on it" as
    // an invariant. One should not exist — the Save control only renders
    // alongside filters — but seeding one would produce a View that flickers
    // in and straight back out.
    if (restored.filters.type === "none") {
      return null;
    }

    return { record, restored };
  });

  const seed = React.useCallback(
    <TData>(
      options: UseResourceViewOptions<TData>,
    ): UseResourceViewOptions<TData> => {
      if (seeded === null) {
        return options;
      }

      const { filters, sort, pageSize, visibleColumns } = seeded.restored;

      return {
        ...options,
        // Narrowed above: the RoboQL arm is the one `ActiveFilters` carries a
        // transpiled `spec` for, and it is filtered out before we get here.
        initialActiveFilters: filters as Exclude<
          typeof filters,
          { type: "roboql" }
        >,
        // A View that recorded no sort/paging/columns defers to the list's own
        // defaults rather than blanking them — `null` means "unstated", not
        // "none". Same rule `useApplyView` follows on the apply path.
        initialSort: sort ?? options.initialSort,
        preferences: {
          columns: visibleColumns ?? options.preferences?.columns,
          rowsPerPage: pageSize ?? options.preferences?.rowsPerPage,
        },
      };
    },
    [seeded],
  );

  return { view: seeded?.record ?? null, seed };
}
